<script setup lang="ts">
import { reactive } from "vue";
import { captureException } from "@sentry/vue";
import { isNumber, formatOptions, debounce } from "@/utils";
import AutoComplete from "@/components/fields/AutoComplete.vue";
import { EditableList } from "@/components/EditableList";

import { Input, Question, DatePicker, Select } from "@/components/fields";
import { Suggestion } from "@/entities/suggestion";
import { useConfigStore } from "@/stores/config";
import { useToast } from "@/services/toast";
import { Medication } from "@/entities/conditions";
import { lookupMedications } from "@/services/api";
import { dosageOptions, freequencyOptions } from "./consts";
import medicationValidator from "./validators";

export type MedicalState = {
    medicationSuggestions: Suggestion[],
    currentMedication: Medication | null,
    showCloseModal: boolean,
    isEdit: boolean,
    alreadySelectedError: string | null
}

export interface Props {
    modelValue: Medication[],
    diagnosisDate: string | null
}

const props = defineProps<Props>();

const emit = defineEmits<{
    (on:"update:modelValue", value: Medication[]): void
}>();

const state: MedicalState = reactive({
    medicationSuggestions: [],
    currentMedication: null,
    showCloseModal: false,
    isEdit: false,
    alreadySelectedError: null,
});

const { toast } = useToast();

const config = useConfigStore();

const validation = medicationValidator(state.currentMedication);

const allMedicationsQuery = async (searchStr: string) => {
    const config = useConfigStore();
    const medications = await lookupMedications({ apiURL: config.apiUrl }, searchStr);
    return medications;
};

const updateSuggestions = debounce(async (searchStr: string) => {
    if (searchStr.length >= 2) {
        try {
            state.medicationSuggestions = await allMedicationsQuery(searchStr);
        } catch (err) {
            captureException(err);
            toast({
                timeout: config.toastTimeout,
                title: "Could not retrieve medications",
                message: "Please try again in a few minutes.",
            });
        }
    }
}, 300);

const applySuggestion = async (medication: Suggestion) => {
    const foundItem = props.modelValue.find(med => med.name === medication.name);
    if (foundItem) {
        state.alreadySelectedError = `${foundItem.name} has already been added`;
    } else {
        state.alreadySelectedError = null;
        state.currentMedication = {
            dosage: null,
            dosageUnits: null,
            frequency: null,
            frequencyUnits: null,
            medicationStartDate: null,
            ...medication,
            syntheticId: Date.now(),
            id: Number(medication.id) ?? 0,
        };
    }
};

const handleUpdate = (items: Medication[]) => {
    emit("update:modelValue", items);
};

const handleUpdateItem = (item: Medication | null) => {
    state.currentMedication = item;
};

const handleSave = (items: Medication[]) => {
    state.currentMedication = null;
    emit("update:modelValue", items);
};

const currentDate = new Date();
const maxDate = currentDate;
</script>
<template>
    <p class="font-weight-bold-col">
        Medication
    </p>
    <p class="font-weight-bold pb-md-1 pb-3 pt-1 mb-2">
        If you are currently taking any medication for this condition please enter the medication name below.
    </p>
    <EditableList
        :current-item="state.currentMedication"
        :items="modelValue"
        :header="state.currentMedication?.name"
        @update:items="handleUpdate"
        @update:item="handleUpdateItem"
        @save="handleSave"
    >
        <template
            v-for="item in modelValue"
            #[item.syntheticId]
            :key="item.syntheticId"
        >
            <b>{{ item.name }}</b>
        </template>
        <template
            v-if="state.currentMedication"
            #default
        >
            <Question
                name="medicationStartDate"
                label="When did you start taking this medication? (Optional)"
            >
                <DatePicker
                    id="medicationStartDate"
                    v-model="state.currentMedication.medicationStartDate"
                    :max-date="maxDate"
                    :min-date="new Date(props.diagnosisDate || 0)"
                    :show-day-field="false"
                />
            </Question>
            <Question
                name="dosage"
                label="How much of this medication do you take?"
            >
                <Input
                    v-model.number="state.currentMedication.dosage"
                    type="number"
                    inputmode="numeric"
                    placeholder="Amount"
                    :filter="isNumber"
                    :validation="validation.dosage"
                />
            </Question>
            <Question
                name="dosageUnits"
                label="dosage units"
                label-class="visually-hidden"
            >
                <Select
                    v-model="state.currentMedication.dosageUnits"
                    placeholder="Measure"
                    :options="formatOptions(dosageOptions) || []"
                    :validation="validation.dosageUnits"
                />
            </Question>
            <Question
                name="frequencyUnits"
                label="How many times do you take this medication?"
            >
                <Select
                    v-model="state.currentMedication.frequencyUnits"
                    placeholder="Daily, Weekly..."
                    :options="formatOptions(freequencyOptions) || []"
                    :validation="validation.frequencyUnits"
                />
            </Question>
            <Question
                v-if="state.currentMedication.frequencyUnits !== 'As Required'"
                name="frequency"
                label="frequency"
                label-class="visually-hidden"
            >
                <Input
                    v-model.number="state.currentMedication.frequency"
                    type="number"
                    placeholder="How Often"
                    inputmode="numeric"
                    :validation="validation.frequency"
                    :filter="isNumber"
                />
            </Question>
        </template>
    </EditableList>
    <Question
        v-if="state.currentMedication === null"
        name="medication-search"
        label="Add medication"
        class="col-md-6 mt-2"
    >
        <AutoComplete
            id="medication-search"
            :suggestions="state.medicationSuggestions.slice(0, 10)"
            not-found-message="Condition not listed"
            :clear-input-after-click="false"
            @update:suggestion="updateSuggestions"
            @click:suggestion="applySuggestion"
        />
        <div v-if="state.alreadySelectedError">
            <div
                class="alert alert-danger d-block mt-2"
            >
                {{ state.alreadySelectedError }}
            </div>
        </div>
    </Question>
</template>
